import moment from 'moment';
import * as React from 'react';
import classNames from 'classnames';
import Button from '../../atoms/Button';

import styles from './PresetDateSelector.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  presetName: PresetOption;
  display?: Display;
  onChange: (args: OnChangeDateProps) => void;
}

export type PresetOption =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | '1week'
  | 'this-month'
  | 'last-month'
  | '1month'
  | '3month'
  | '6month'
  | '1year'
  | string;

export interface Display {
  today?: boolean;
  yesterday?: boolean;
  custom?: boolean;
  current?: boolean;
}

export interface DateInterval {
  start: string;
  end: string;
}

export interface OnChangeDateProps {
  from: string;
  to: string;
  presetName: PresetOption;
}

const PresetDateSelector: React.FunctionComponent<Props> = ({
  className,
  presetName = '3month',
  display = {
    // Change these defaults when v1 is deleted
    today: true,
    yesterday: false,
    custom: true,
    current: false,
  },
  onChange,
  ...htmlElementProps
}) => {
  /**
   * Handles date selection using buttons
   */
  const changePresetDate = (type: PresetOption) => {
    const today = moment();

    let startDate;
    let endDate;
    switch (type) {
      case 'today':
        startDate = today.startOf('day');
        endDate = moment().endOf('day');
        break;
      case 'yesterday':
        startDate = today.subtract(1, 'days').startOf('day');
        endDate = moment().subtract(1, 'days').endOf('day');
        break;
      case 'this-week':
        startDate = today.startOf('isoWeek');
        endDate = moment().endOf('day');
        break;
      case '1week':
        startDate = today.subtract(6, 'days').startOf('day');
        endDate = moment().endOf('day');
        break;
      case 'this-month':
        startDate = today.startOf('month');
        endDate = moment().endOf('day');
        break;
      case 'last-month':
        startDate = today.subtract(1, 'months').startOf('month');
        endDate = moment().subtract(1, 'months').endOf('month');
        break;
      case '1month':
        startDate = today.subtract(1, 'months').startOf('day');
        endDate = moment().endOf('day');
        break;
      case '3month':
        startDate = today.subtract(3, 'months').startOf('day');
        endDate = moment().endOf('day');
        break;
      case '6month':
        startDate = today.subtract(6, 'months').startOf('day');
        endDate = moment().endOf('day');
        break;
      case '1year':
      default:
        startDate = today.subtract(1, 'years').startOf('day');
        endDate = moment().endOf('day');
    }

    onChange({
      from: startDate.toISOString(),
      to: endDate.toISOString(),
      presetName: type,
    });
  };

  return (
    <div
      className={classNames(styles.wrapper, className)}
      {...htmlElementProps}
    >
      {display.today && (
        <Button
          size="sm"
          variant={presetName === 'today' ? 'default' : 'outline'}
          onClick={() => changePresetDate('today')}
        >
          hoje
        </Button>
      )}

      {display.yesterday && (
        <Button
          size="sm"
          variant={presetName === 'yesterday' ? 'default' : 'outline'}
          onClick={() => changePresetDate('yesterday')}
        >
          ontem
        </Button>
      )}

      {display.current && (
        <Button
          size="sm"
          variant={presetName === 'this-week' ? 'default' : 'outline'}
          onClick={() => changePresetDate('this-week')}
        >
          esta semana
        </Button>
      )}
      {display.current && (
        <Button
          size="sm"
          variant={presetName === 'this-month' ? 'default' : 'outline'}
          onClick={() => changePresetDate('this-month')}
        >
          este mês
        </Button>
      )}
      {display.current && (
        <Button
          size="sm"
          variant={presetName === 'last-month' ? 'default' : 'outline'}
          onClick={() => changePresetDate('last-month')}
        >
          mês anterior
        </Button>
      )}

      {display.current === false && (
        <Button
          size="sm"
          variant={presetName === '1week' ? 'default' : 'outline'}
          onClick={() => changePresetDate('1week')}
        >
          1 semana
        </Button>
      )}
      {display.current === false && (
        <Button
          size="sm"
          variant={presetName === '1month' ? 'default' : 'outline'}
          onClick={() => changePresetDate('1month')}
        >
          1 mês
        </Button>
      )}
      <Button
        size="sm"
        variant={presetName === '3month' ? 'default' : 'outline'}
        onClick={() => changePresetDate('3month')}
      >
        3 meses
      </Button>
      <Button
        size="sm"
        variant={presetName === '6month' ? 'default' : 'outline'}
        onClick={() => changePresetDate('6month')}
      >
        6 meses
      </Button>
      {display.current === false && (
        <Button
          size="sm"
          variant={presetName === '1year' ? 'default' : 'outline'}
          onClick={() => changePresetDate('1year')}
        >
          12 meses
        </Button>
      )}
    </div>
  );
};

export default PresetDateSelector;
